import appSettings from 'wanderu-config';
import {
    selectLocale,
} from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import { TixService } from 'wanderu-service-wapi';

import frontEndValidations from '../../../frontEndValidations';
import {
    actions as bookingActions,
    selectors as bookingSelectors,
} from '../../appstate';
import { submitFormAction } from '../submitForm';
import { removeValidationForUnmodifiedFields } from '../utils';
import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';

const logger = Logger.getLogger('wanderu-app-booking:validateForm');

let controller;

export async function backendValidations(state, opts = { cancelable: true }) {
    if (controller) {
        controller.abort();
    }

    let signal;
    if (opts.cancelable) {
        controller = new AbortController();
        signal = controller.signal;
    }

    const locale = selectLocale(state);

    const {
        tripIds,
        itineraryId,
        seats,
        wheelchairs,
        bookingSessionId,
        tokenMap = {},
    } = bookingSelectors.selectBookingInfo(state);

    const form = bookingSelectors.selectSlimFormData(state);

    const body = {
        accessibility: {
            wheelchairs,
        },
        booking_session_id: bookingSessionId,
        cacheTokens: Object.values(tokenMap),
        form_submission: { fields: form.fields },
        itinerary_id: itineraryId,
        locale,
        metadata: {
            form_version: form.meta.formVersion,
        },
        passengers: seats,
        trip_ids: tripIds,
    };

    const { result: { fields } } = await TixService.post('v3/book/form/validate', {
        body,
        signal,
    }, { suppressLogging: true });

    return fields;
}

function validateForm(opts = {}) {
    return async (dispatch, getState) => {
        const { isFormSubmission = false } = opts;

        let error;
        let meta;

        const bookingForm = bookingSelectors.selectFormData(getState());

        const {
            validatedForm,
            isValid,
        } = frontEndValidations(bookingForm);

        if (!isValid) {
            dispatch(bookingActions.setValidations({ fields: validatedForm }));
            return;
        }

        dispatch(actions.startWork());

        try {
            const fields = await backendValidations(getState());
            const validationFields = removeValidationForUnmodifiedFields(bookingForm, fields) || [];

            dispatch(bookingActions.setValidations({ fields: validationFields }));

            if (bookingSelectors.selectAllFieldsAreValid(getState()) && !isFormSubmission) {
                dispatch(submitFormAction.reset());
            }
        } catch (ex) {
            if (ex.name !== 'AbortError') {
                const domainUserInfo = appSettings.env.isClient
                    ? SnowplowService.getDomainUserInfo()
                    : {};

                logger.error('post', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                });
            }

            error = {
                code: ex.code,
                message: ex.message,
                name: ex.name,
            };
        } finally {
            // on abort, prevent the endWork action in order to force the UI
            // to update on a subsequent successful action
            if (error?.name !== 'AbortError') {
                dispatch(actions.endWork(meta, error));
            }
        }
    };
}

const validateFormAction = {
    validate: validateForm,
    ...selectors,
    ...actions,
};

export {
    validateFormAction,
};

const validateFormSlice = {
    [namespace]: reducer,
};

export default validateFormSlice;
